import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { gql } from 'apollo-angular';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { GetCollectionsQuery, GetCollectionsQueryVariables } from '../../../common/generated-types';
import { GET_COLLECTIONS } from '../../../common/graphql/documents.graphql';
import { DataService } from '../../../core/providers/data/data.service';

import { arrayToTree, RootNode, TreeNode } from './array-to-tree';

type CollectionItem = GetCollectionsQuery['collections']['items'][number];

@Component({
    selector: 'vsf-collections-menu',
    templateUrl: './collections-menu.component.html',
    styleUrls: ['./collections-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionsMenuComponent implements OnInit, OnDestroy {
    collectionTree$: Observable<RootNode<CollectionItem>>;
    sectorCollections$: Observable<CollectionItem[]>;
    hasBestSellers$: Observable<boolean>;
    hasBrands$: Observable<boolean>;
    megaMenuOpen = false;
    sectorsMenuOpen = false;
    hoveredCollection: TreeNode<CollectionItem> | null = null;
    expandedCollections = new Set<string>();

    private destroy$ = new Subject<void>();
    private closeTimeout: any;
    private sectorsCloseTimeout: any;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private dataService: DataService,
        private router: Router,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        const allCollections$ = this.dataService.query<GetCollectionsQuery, GetCollectionsQueryVariables>(GET_COLLECTIONS, {
            options: { take: 50 },
        }).pipe(takeUntil(this.destroy$));

        this.collectionTree$ = allCollections$.pipe(
            map(data => arrayToTree(data.collections.items.filter(c => !(c as any).customFields?.isSector))),
        );

        this.sectorCollections$ = allCollections$.pipe(
            map(data => data.collections.items.filter(c => (c as any).customFields?.isSector)),
        );

        this.hasBestSellers$ = this.dataService.query<any>(GET_BEST_SELLER_PRODUCTS, {
            take: 1,
        }).pipe(
            map(data => (data.bestSellerProducts?.length ?? 0) > 0),
            takeUntil(this.destroy$),
        );

        this.hasBrands$ = this.dataService.query<any>(GET_BRAND_ITEMS).pipe(
            map(data => (data.brandItems?.length ?? 0) > 0),
            takeUntil(this.destroy$),
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
        if (this.sectorsCloseTimeout) {
            clearTimeout(this.sectorsCloseTimeout);
        }
    }

    openMegaMenu() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
        this.megaMenuOpen = true;
        this.cdr.markForCheck();
    }

    closeMegaMenuDelayed() {
        this.closeTimeout = setTimeout(() => {
            this.megaMenuOpen = false;
            this.hoveredCollection = null;
            this.cdr.markForCheck();
        }, 150);
    }

    keepMegaMenuOpen() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
    }

    closeMegaMenu() {
        this.megaMenuOpen = false;
        this.hoveredCollection = null;
        this.expandedCollections.clear();
        this.cdr.markForCheck();
    }

    openSectorsMenu() {
        if (this.sectorsCloseTimeout) {
            clearTimeout(this.sectorsCloseTimeout);
            this.sectorsCloseTimeout = null;
        }
        this.sectorsMenuOpen = true;
        this.cdr.markForCheck();
    }

    closeSectorsMenuDelayed() {
        this.sectorsCloseTimeout = setTimeout(() => {
            this.sectorsMenuOpen = false;
            this.cdr.markForCheck();
        }, 150);
    }

    keepSectorsMenuOpen() {
        if (this.sectorsCloseTimeout) {
            clearTimeout(this.sectorsCloseTimeout);
            this.sectorsCloseTimeout = null;
        }
    }

    closeSectorsMenu() {
        this.sectorsMenuOpen = false;
        this.cdr.markForCheck();
    }

    onHoverMainCollection(collection: TreeNode<CollectionItem>) {
        this.hoveredCollection = collection;
        this.cdr.markForCheck();
    }

    toggleExpanded(id: string, event: Event) {
        event.stopPropagation();
        if (this.expandedCollections.has(id)) {
            this.expandedCollections.delete(id);
        } else {
            this.expandedCollections.add(id);
        }
        this.cdr.markForCheck();
    }

    isExpanded(id: string): boolean {
        return this.expandedCollections.has(id);
    }

    scrollToSection(sectionId: string) {
        const currentUrl = this.router.url.split('#')[0];
        if (currentUrl === '/' || currentUrl === '') {
            // Already on home page — scroll directly
            setTimeout(() => {
                const el = this.document.getElementById(sectionId);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 50);
        } else {
            // Navigate to home page first, then poll until section exists
            this.router.navigate(['/']).then(() => {
                this.waitAndScroll(sectionId);
            });
        }
    }

    private waitAndScroll(sectionId: string, attempts = 0) {
        const el = this.document.getElementById(sectionId);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 25) {
            setTimeout(() => this.waitAndScroll(sectionId, attempts + 1), 100);
        }
    }

    navigateTo(slug: string) {
        this.closeMegaMenu();
        this.expandedCollections.clear();
        this.router.navigate(['/category', slug]);
    }
}

const GET_BEST_SELLER_PRODUCTS = gql`
    query GetBestSellerProductsCheck($take: Int) {
        bestSellerProducts(take: $take) {
            id
        }
    }
`;

const GET_BRAND_ITEMS = gql`
    query GetBrandItemsCheck {
        brandItems {
            id
        }
    }
`;
